import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { useStore } from '../../context/StoreContext';

const AdminVideos = () => {
  const { videos, saveVideo, deleteVideo } = useStore();
  const [editingVideo, setEditingVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openNew = () => {
    setEditingVideo({
      title: '',
      description: '',
      category: 'The ABCMP Series', // Default Series
      tags: '',
      thumbnailUrl: '',
      duration: '',
      accessLevel: 'free',
      videoUrl: '', // Added new field initialization
      embedHtml: '',
      status: 'draft'
    });
    setIsModalOpen(true);
  };

  const openEdit = (video) => {
    setEditingVideo({ 
      ...video, 
      tags: video.tags ? video.tags.join(',') : '',
      videoUrl: video.videoUrl || '' // Ensure it exists
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const tagsArray = typeof editingVideo.tags === 'string' 
      ? editingVideo.tags.split(',').map(t => t.trim()).filter(Boolean) 
      : editingVideo.tags;
      
    saveVideo({ ...editingVideo, tags: tagsArray });
    setIsModalOpen(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">Content Library</h1>
          <p className="text-muted text-sm">Manage Series, Videos, Links, and Thumbnails</p>
        </div>
        <button 
          onClick={openNew}
          className="bg-rocket hover:bg-rocket-hover text-white px-4 py-2 rounded-md flex items-center text-sm font-semibold"
        >
          <SafeIcon name="Plus" className="w-4 h-4 mr-2" />
          Add Video
        </button>
      </div>

      <div className="bg-dark-surface border border-dark-border rounded-xl overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-dark-card border-b border-dark-border text-muted text-sm">
              <th className="p-4 font-medium">Video Title</th>
              <th className="p-4 font-medium">Series / Collection</th>
              <th className="p-4 font-medium">Access</th>
              <th className="p-4 font-medium">Status</th>
              <th className="p-4 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {videos.map(video => (
              <tr key={video.id} className="border-b border-dark-border hover:bg-dark-card/50 transition-colors">
                <td className="p-4 flex items-center space-x-3">
                  <img src={video.thumbnailUrl} alt="" className="w-16 h-9 object-cover rounded bg-dark-bg border border-white/10" />
                  <div>
                    <p className="font-semibold text-sm max-w-xs truncate">{video.title}</p>
                    <p className="text-xs text-muted">{video.duration || 'N/A'}</p>
                  </div>
                </td>
                <td className="p-4 text-sm text-muted">{video.category}</td>
                <td className="p-4 text-sm">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${video.accessLevel === 'pro' ? 'bg-rocket/20 text-rocket' : 'bg-green-500/20 text-green-400'}`}>
                    {video.accessLevel.toUpperCase()}
                  </span>
                </td>
                <td className="p-4 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs ${video.status === 'published' ? 'bg-white/10 text-white' : 'bg-dark-bg text-muted border border-dark-border'}`}>
                    {video.status}
                  </span>
                </td>
                <td className="p-4 text-right space-x-2">
                  <button onClick={() => openEdit(video)} className="p-2 text-muted hover:text-white bg-dark-bg rounded border border-dark-border transition-colors">
                    <SafeIcon name="Edit2" className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteVideo(video.id)} className="p-2 text-danger hover:bg-danger/10 bg-dark-bg rounded border border-dark-border transition-colors">
                    <SafeIcon name="Trash2" className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-dark-surface border border-dark-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-dark-border flex justify-between items-center bg-dark-surface sticky top-0 z-10">
              <h2 className="text-xl font-bold">{editingVideo.id ? 'Edit Video' : 'Add New Video'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-muted hover:text-white"><SafeIcon name="X" className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-muted mb-1">Video Title</label>
                  <input 
                    required
                    type="text" 
                    value={editingVideo.title} 
                    onChange={e => setEditingVideo({ ...editingVideo, title: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none"
                    placeholder="e.g., Candlestick Basics"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm text-muted mb-1">Series Name / Category Row</label>
                  <input 
                    required
                    type="text" 
                    value={editingVideo.category} 
                    onChange={e => setEditingVideo({ ...editingVideo, category: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none"
                    placeholder="e.g., Trending in Trader Education"
                  />
                  <p className="text-xs text-muted mt-1">This groups the video into a specific horizontal Netflix-style row on the homepage.</p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm text-muted mb-1">Description</label>
                  <textarea 
                    required
                    value={editingVideo.description} 
                    onChange={e => setEditingVideo({ ...editingVideo, description: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none h-20"
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm text-muted mb-1">Thumbnail Image URL</label>
                  <input 
                    required
                    type="url" 
                    value={editingVideo.thumbnailUrl} 
                    onChange={e => setEditingVideo({ ...editingVideo, thumbnailUrl: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none"
                    placeholder="https://images.unsplash.com/..."
                  />
                </div>

                <div className="col-span-2 md:col-span-1">
                  <label className="block text-sm text-muted mb-1">Duration (e.g., 45m)</label>
                  <input 
                    type="text" 
                    value={editingVideo.duration} 
                    onChange={e => setEditingVideo({ ...editingVideo, duration: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-muted mb-1">Access Level</label>
                  <select 
                    value={editingVideo.accessLevel} 
                    onChange={e => setEditingVideo({ ...editingVideo, accessLevel: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none"
                  >
                    <option value="free">Free Access</option>
                    <option value="pro">Pro (Requires Subscription)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-muted mb-1">Publish Status</label>
                  <select 
                    value={editingVideo.status} 
                    onChange={e => setEditingVideo({ ...editingVideo, status: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none"
                  >
                    <option value="draft">Draft (Hidden)</option>
                    <option value="published">Published (Live)</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm text-muted mb-1">Tags (Comma Separated)</label>
                  <input 
                    type="text" 
                    value={editingVideo.tags} 
                    onChange={e => setEditingVideo({ ...editingVideo, tags: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none"
                    placeholder="Strategy, Beginner, HD"
                  />
                </div>

                {/* --- NEW VIDEO URL FIELD --- */}
                <div className="col-span-2 mt-2">
                  <div className="p-4 border border-rocket/30 bg-rocket/5 rounded-lg">
                    <label className="block text-sm font-bold text-white mb-1 flex items-center">
                      <SafeIcon name="Link" className="w-4 h-4 mr-2 text-rocket" />
                      Video Source URL (Recommended)
                    </label>
                    <input 
                      type="url" 
                      value={editingVideo.videoUrl || ''} 
                      onChange={e => setEditingVideo({ ...editingVideo, videoUrl: e.target.value })}
                      className="w-full bg-dark-bg border border-dark-border rounded p-3 focus:border-rocket outline-none text-white"
                      placeholder="e.g., https://www.youtube.com/watch?v=..."
                    />
                    <p className="text-xs text-muted mt-2">
                      Simply paste a standard <b>YouTube</b>, <b>Vimeo</b>, or direct <b>.mp4</b> link here. We will automatically generate the player for you!
                    </p>
                  </div>
                </div>

                <div className="col-span-2 mt-2">
                  <div className="flex items-center space-x-2 mb-1">
                    <label className="block text-sm text-muted">Advanced: Custom Embed HTML</label>
                    <span className="text-[10px] bg-dark-bg border border-dark-border px-1.5 py-0.5 rounded text-muted">Overrides URL</span>
                  </div>
                  <textarea 
                    value={editingVideo.embedHtml} 
                    onChange={e => setEditingVideo({ ...editingVideo, embedHtml: e.target.value })}
                    className="w-full bg-dark-bg border border-dark-border rounded p-2 focus:border-rocket outline-none h-20 font-mono text-xs"
                    placeholder='<iframe src="..." width="100%" height="100%" frameborder="0" allowfullscreen></iframe>'
                  />
                </div>

              </div>

              <div className="pt-4 flex justify-end space-x-3 border-t border-dark-border mt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-dark-border rounded hover:bg-dark-card">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-rocket text-white rounded hover:bg-rocket-hover font-bold shadow-lg">Save Video</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVideos;